import React, {useEffect} from 'react';

import {connect} from 'react-redux';

import {DetailLoadingPage} from 'app/general/loading/DetailLoadingPage';
import {loadPolicyByHashid} from 'app/policies/hocs';
import {loadProcedureByHashid} from 'app/procedures/hocs';
import {loadProcessByHashid} from 'app/process/hocs';
import {useQuiz, useQuizVersions} from 'app/quizzes/api';
import {goToDocumentEditPage, goToDocumentEditPageRTK} from 'app/version/utils';

import type {UseQuery} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type {QueryDefinition} from '@reduxjs/toolkit/query/react';
import type {LoadByHashidInjectedProps} from 'app/general/loadByHashid';
import type {VersionableDocumentTypes} from 'app/general/types';
import type {PolicyType} from 'app/policies/types';
import type {ProcedureType} from 'app/procedures/types';
import type {ProcessType} from 'app/process/types';
import type {AppDispatchType} from 'app/store/AppStore';
import type {ComponentType} from 'react';
import type {WithRouterProps} from 'react-router';
import type {
  ListResponseType,
  QuizSerializedType,
  QuizVersionSerializedType,
} from 'sweet-types';

type RequiredProps = {
  readonly params: {
    readonly hashid: string;
  };
};

type LoadByHashIdFuncType = <
  Config extends RequiredProps,
  T extends {
    id: number;
  }
>(
  Component: ComponentType<Config & LoadByHashidInjectedProps<T>>
) => ComponentType<Config>;

function ForwardToDocumentEditPageFactory<
  T extends {
    id: number;
  }
>(loadByHashId: LoadByHashIdFuncType, contentType: VersionableDocumentTypes) {
  type InjectedRouterType = WithRouterProps<
    Record<string, never>,
    {
      hashid: string;
    }
  >;
  type OwnProps = InjectedRouterType;
  type LoadedByHashIdOwnProps = OwnProps & LoadByHashidInjectedProps<T>;
  type StateProps = {
    contentType: VersionableDocumentTypes;
  };
  type DispatchProps = {
    goToDocumentEditPage: (
      ct: VersionableDocumentTypes,
      id: number,
      replace: boolean
    ) => (dispatch: AppDispatchType) => unknown;
  };
  type Props = LoadedByHashIdOwnProps & StateProps & DispatchProps;

  class BaseForwardToDocumentEditPage extends React.PureComponent<Props> {
    componentDidMount() {
      const {
        fetchedByHashid: {object},
        contentType,
        goToDocumentEditPage,
      } = this.props;
      return goToDocumentEditPage(contentType, object.id, true);
    }

    render() {
      return <DetailLoadingPage />;
    }
  }

  const mapStateToProps = (): StateProps => ({
    contentType,
  });

  // @ts-expect-error lets rewrite this later
  return loadByHashId<OwnProps, T>(
    connect<Props, LoadedByHashIdOwnProps, StateProps, DispatchProps>(
      // @ts-expect-error lets rewrite this later
      mapStateToProps,
      {
        goToDocumentEditPage,
      }
    )(BaseForwardToDocumentEditPage)
  );
}

export const ForwardToPolicyEditPage =
  ForwardToDocumentEditPageFactory<PolicyType>(loadPolicyByHashid, 'policy');
export const ForwardToProcedureEditPage =
  ForwardToDocumentEditPageFactory<ProcedureType>(
    // @ts-expect-error This whole structure needs to eb re-written..later
    loadProcedureByHashid,
    'procedure'
  );
export const ForwardToProcessEditPage =
  ForwardToDocumentEditPageFactory<ProcessType>(
    // @ts-expect-error This whole structure needs to eb re-written..later
    loadProcessByHashid,
    'proceduregroup'
  );

type RouterProps = WithRouterProps<{hashid: string}>;

type ForwardProps<T, V> = {
  useData: UseQuery<QueryDefinition<string, any, any, T, 'sweetAPI'>>;
  useLatestVersionData: UseQuery<
    QueryDefinition<any, any, any, ListResponseType<V>, 'sweetAPI'>
  >;
};

// A generic page forwarding to /edit/ component. to be used by RTK query code.
const ForwardToDocumentEditPage = <
  T extends {id: number} | {id: string},
  V extends {html_url: string}
>({
  useData,
  useLatestVersionData,
  params,
  versionParam,
}: ForwardProps<T, V> &
  Pick<RouterProps, 'params'> & {versionParam: string}) => {
  const {data: object} = useData(params.hashid);
  const {version} = useLatestVersionData(
    {[versionParam]: object?.id},
    {
      skip: object?.id === null,
      selectFromResult: ({data}) => ({version: data?.results[0]}),
    }
  );
  useEffect(() => {
    if (version) {
      goToDocumentEditPageRTK({version});
    }
  }, [version]);
  return <DetailLoadingPage />;
};

export const ForwardToQuizEditPage = ({params}: RouterProps) => (
  <ForwardToDocumentEditPage<QuizSerializedType, QuizVersionSerializedType>
    useData={useQuiz}
    useLatestVersionData={useQuizVersions}
    params={params}
    versionParam="quizId"
  />
);
